import { dispatchLoadingEvent } from '@epilot/journey-logic-commons'
import type { StepId } from '@epilot/journey-logic-commons/src/types/next'
// @ts-expect-error until react types reflect v18
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react'

import { navigateBack } from '../store/actions/navigateBack'
import { navigateNext } from '../store/actions/navigateNext'
import {
  selectCurrentStep,
  selectLastDesiredState
} from '../store/selectors/runtime'
import type { DesiredState } from '../store/types'

import { useAppStoreSelector } from './useAppStoreSelector'
import { useLogicsEngine } from './useLogicsEngine'
import { usePrevious } from './usePrevious'
import { useTransientState } from './useTransientState'

/**
 * Duration of a Step transition, in miliseconds.
 * Useful to allow for animations to complete before performing state cleanup or some other transition.
 */
export const STEP_TRANSITION_DURATION = 400

enum DIRECTION {
  NEXT = 'next',
  BACK = 'back'
}

type TransitionLogicArgs = {
  /** Which Step we're currently rendering */
  currentStepId?: StepId
  /** Which Step we're coming from, if any */
  lastCurrentStepId?: StepId
  /** What is the last desired state we want to transition to */
  desiredState?: DesiredState
  /** In which direction we're navigating, if any */
  direction?: DIRECTION
  /** In which direction we were navigating previously, if any */
  lastDirection?: DIRECTION
  /** Which is the next intended Step Id, unless negated by logics */
  intendedNextStepId?: StepId
}

/**
 * Evalutes the given context to determine if a Step transition should be performed.
 * Dispatches the appropriate actions to navigate to the next or previous Step.
 *
 * @returns {boolean} whereas `true` indicates a state cleanup needs to be performed afterwards
 */
export const transitionLogic = (args: TransitionLogicArgs): boolean => {
  // if navigating backwards
  if (args.direction === DIRECTION.BACK) {
    navigateBack(undefined)

    return true
  }

  if (
    // if navigating next
    args.direction === DIRECTION.NEXT ||
    // or there is a Step override
    (args.desiredState?.nextStepOverride &&
      // and overriden Step isn't the current or previous Step
      ![args.currentStepId, args.lastCurrentStepId].includes(
        args.desiredState?.nextStepOverride
      ) &&
      // and we weren't navigating backwards previously
      args.lastDirection !== DIRECTION.BACK)
  ) {
    navigateNext({ intendedStepId: args.intendedNextStepId })

    return true
  }

  return false
}

export const useStepTransitions = () => {
  const logicsRunner = useLogicsEngine()
  const [locked, setLocked] = useState<boolean>(false)
  const currentStepId = useAppStoreSelector(selectCurrentStep)?.id
  const lastCurrentStepId = usePrevious(currentStepId)
  const desiredState = useAppStoreSelector(selectLastDesiredState)
  const [intendedNextStepId, setIntendedNextStepId] = useState<
    StepId | undefined
  >()
  const [direction, setDirection] = useState<DIRECTION | undefined>(undefined)
  const lastDirection = usePrevious(direction)
  const [isTransitioning, startTransition] = useTransition()
  const [animationDirection, setAnimationDirection] = useTransientState<
    DIRECTION | undefined
  >(undefined, STEP_TRANSITION_DURATION)

  const navigateBack = useCallback(() => {
    setLocked(true)

    startTransition(() => {
      setDirection(DIRECTION.BACK)
      setIntendedNextStepId(undefined) // unset
      setLocked(false)
    })
  }, [])

  const navigateNext = useCallback(
    async (intendedStepId?: StepId) => {
      setLocked(true)
      dispatchLoadingEvent(true)

      await logicsRunner()

      startTransition(() => {
        setDirection(DIRECTION.NEXT)
        setIntendedNextStepId(intendedStepId)
        setLocked(false)
        dispatchLoadingEvent(false)
      })
    },
    [logicsRunner]
  )

  useEffect(
    // syncs the animation direction when direction changes
    () => {
      if (direction) setAnimationDirection(direction)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [direction]
  )

  useEffect(() => {
    if (locked) return // wont run

    startTransition(() => {
      if (
        transitionLogic({
          currentStepId, // not a dependency
          lastCurrentStepId, // not a dependency
          intendedNextStepId,
          desiredState,
          direction,
          lastDirection // not a dependency
        })
      ) {
        // cleanup
        setDirection(undefined)
        setIntendedNextStepId(undefined)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredState, direction, intendedNextStepId, locked])

  return useMemo(
    () => ({
      goBack: navigateBack,
      goNext: navigateNext,
      animatingBackwards: animationDirection === DIRECTION.BACK,
      animatingForward: animationDirection === DIRECTION.NEXT,
      isTransitioning
    }),
    [navigateBack, navigateNext, animationDirection, isTransitioning]
  )
}
